import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { mockData } from '../mock';
import { Mail, Phone, MapPin, Send, Instagram, Facebook } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.3 });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        to: 'comercial@imperioice.com.br'
      });
      
      setSubmitStatus('success');
      alert('✅ Mensagem enviada com sucesso! Entraremos em contato em breve.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitStatus('error');
      alert('❌ Erro ao enviar mensagem. Por favor, tente novamente ou entre em contato pelo WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" ref={containerRef} className="relative py-32 px-6 bg-gradient-to-b from-[#0a1628] to-[#0f1f3a] overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-[#7dd3fc]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#7dd3fc]/5 rounded-full blur-3xl" />

      <div className="container mx-auto max-w-7xl relative z-10">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
            Entre em <span className="text-[#7dd3fc]">Contato</span>
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Estamos prontos para atender você. Fale conosco!
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-3xl font-bold text-white mb-8">
                Fale Conosco
              </h3>
              <p className="text-white/60 leading-relaxed mb-8">
                Nossa equipe está disponível para esclarecer dúvidas, fazer orçamentos e atender suas necessidades.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <motion.div
                whileHover={{ x: 10 }}
                className="flex items-center gap-4 group"
              >
                <div className="w-14 h-14 bg-[#7dd3fc]/10 rounded-2xl flex items-center justify-center group-hover:bg-[#7dd3fc]/20 transition-colors duration-300">
                  <Mail className="text-[#7dd3fc]" size={24} />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Email</p>
                  <a href={`mailto:${mockData.contact.email}`} className="text-white text-lg hover:text-[#7dd3fc] transition-colors duration-300" data-testid="contact-email-link">
                    {mockData.contact.email}
                  </a>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ x: 10 }}
                className="flex items-center gap-4 group"
              >
                <div className="w-14 h-14 bg-[#7dd3fc]/10 rounded-2xl flex items-center justify-center group-hover:bg-[#7dd3fc]/20 transition-colors duration-300">
                  <Phone className="text-[#7dd3fc]" size={24} />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Telefone</p>
                  <p className="text-white text-lg">{mockData.contact.phone}</p>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ x: 10 }}
                className="flex items-center gap-4 group"
              >
                <div className="w-14 h-14 bg-[#7dd3fc]/10 rounded-2xl flex items-center justify-center group-hover:bg-[#7dd3fc]/20 transition-colors duration-300">
                  <MapPin className="text-[#7dd3fc]" size={24} />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Localização</p>
                  <p className="text-white text-lg">{mockData.contact.address}</p>
                </div>
              </motion.div>
            </div>

            {/* Social Media */}
            <div className="pt-8">
              <p className="text-white/50 text-sm mb-4">Siga-nos nas redes sociais</p>
              <div className="flex gap-4">
                <motion.a
                  href="https://instagram.com/imperio.ice__"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -5 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-12 h-12 bg-[#7dd3fc]/10 rounded-xl flex items-center justify-center hover:bg-[#7dd3fc]/20 transition-colors duration-300"
                >
                  <Instagram className="text-[#7dd3fc]" size={20} />
                </motion.a>
                <motion.a
                  href="#"
                  whileHover={{ scale: 1.1, y: -5 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-12 h-12 bg-[#7dd3fc]/10 rounded-xl flex items-center justify-center hover:bg-[#7dd3fc]/20 transition-colors duration-300"
                >
                  <Facebook className="text-[#7dd3fc]" size={20} />
                </motion.a>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative p-8 md:p-12 bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-white/70 text-sm mb-2">
                  Nome Completo
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#7dd3fc]/50 transition-colors duration-300"
                  placeholder="Seu nome"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-white/70 text-sm mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#7dd3fc]/50 transition-colors duration-300"
                  placeholder="seu@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-white/70 text-sm mb-2">
                  Telefone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#7dd3fc]/50 transition-colors duration-300"
                  placeholder="(11) 99999-9999"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-white/70 text-sm mb-2">
                  Mensagem
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#7dd3fc]/50 transition-colors duration-300 resize-none"
                  placeholder="Como podemos ajudar você?"
                />
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: isSubmitting ? 1 : 1.02, y: isSubmitting ? 0 : -2 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                className={`w-full py-5 bg-gradient-to-r from-[#7dd3fc] to-[#bae6fd] text-[#0a1628] font-bold text-lg rounded-xl shadow-xl shadow-[#7dd3fc]/30 hover:shadow-2xl hover:shadow-[#7dd3fc]/40 transition-all duration-300 flex items-center justify-center gap-2 ${
                  isSubmitting ? 'opacity-70 cursor-wait' : ''
                }`}
              >
                <span>{isSubmitting ? 'Enviando...' : 'Enviar Mensagem'}</span>
                <Send size={20} className={isSubmitting ? 'animate-pulse' : ''} />
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
